/**
 * Recently viewed (product.md item 18). Records the product ids the shopper
 * opens in local storage (newest first, 12 kept) and fills the
 * <product-recently-viewed> section through the Section Rendering API on the
 * search page, so the cards are the theme's own card-product markup and
 * nothing is rebuilt in JS. The element stays hidden until it has cards.
 */
if (!customElements.get('product-recently-viewed')) {
  customElements.define(
    'product-recently-viewed',
    class ProductRecentlyViewed extends HTMLElement {
      static STORAGE_KEY = 'newhill:recently-viewed';
      static KEEP = 12;

      connectedCallback() {
        this.ids = this.read();
        const current = this.dataset.productId;
        if (current) {
          this.ids = this.ids.filter((id) => id !== current);
          this.write([current, ...this.ids].slice(0, ProductRecentlyViewed.KEEP));
        }
        if (!this.ids.length) return;

        // The element itself starts hidden (display: none) and an
        // IntersectionObserver never reports a display: none target as
        // intersecting, so observe its rendered, zero-height section wrapper
        // instead and load when that comes within 600px of the viewport.
        const sentinel = this.closest('.shopify-section') || this.parentElement || this;
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(sentinel);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      read() {
        try {
          const stored = JSON.parse(window.localStorage.getItem(ProductRecentlyViewed.STORAGE_KEY) || '[]');
          return Array.isArray(stored) ? stored.map(String) : [];
        } catch {
          return [];
        }
      }

      write(ids) {
        try {
          window.localStorage.setItem(ProductRecentlyViewed.STORAGE_KEY, JSON.stringify(ids));
        } catch {
          // Private mode or storage full: nothing to record.
        }
      }

      load() {
        const limit = Number.parseInt(this.dataset.limit, 10) || 4;
        const ids = this.ids.slice(0, limit);
        const query = ids.map((id) => `id:${id}`).join(' OR ');
        const url = `${this.dataset.searchUrl}?type=product&q=${encodeURIComponent(query)}&section_id=${this.dataset.sectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const rendered = html.querySelector('product-recently-viewed');
            if (!rendered || !rendered.querySelector('.grid__item')) return;
            this.innerHTML = rendered.innerHTML;

            // Search returns by relevance; restore the viewing order.
            const list = this.querySelector('.product-rail__grid');
            if (list) {
              ids.forEach((id) => {
                const item = list.querySelector(`[data-product-id="${id}"]`);
                if (item) list.appendChild(item);
              });
            }
            this.hidden = false;
          })
          .catch((error) => console.error(error));
      }
    }
  );
}
